import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject, takeUntil } from 'rxjs';
import { IdeasApiService } from '../../shared/api/ideas-api.service';
import { CategoryDto } from '../../shared/api/ideas-api.types';

@Component({
  selector: 'app-submit-idea',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './submit-idea.component.html',
  styleUrl: './submit-idea.component.css'
})
export class SubmitIdeaComponent implements OnInit, OnDestroy {
  form: FormGroup;
  categories: CategoryDto[] = [];
  loadingCategories = false;
  loadingIdea = false;
  editingIdeaId: string | null = null;
  saving = false;
  errorMessage = '';

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly api: IdeasApiService,
    private readonly router: Router,
    private readonly route: ActivatedRoute
  ) {
    this.form = this.fb.group({
      title: ['', [Validators.required, Validators.minLength(3)]],
      categoryId: ['', [Validators.required]],
      briefDescription: ['', [Validators.maxLength(2000)]],
      problemOpportunity: [''],
      proposedSolution: [''],
      impactScope: this.fb.group({
        myTeam: [false],
        myDepartment: [false],
        companyWide: [false],
        customers: [false],
        product: [false]
      }),
      expectedImpactType: this.fb.group({
        timeSaved: [false],
        costSaved: [false],
        revenue: [false],
        quality: [false],
        riskReduction: [false],
        employeeExperience: [false]
      }),
      estimatedEffort: ['medium'],
      ownerDepartment: ['product'],
      tags: [''],
      submitAnonymously: [false]
    });
  }

  ngOnInit(): void {
    this.editingIdeaId = this.route.snapshot.queryParamMap.get('ideaId');

    this.loadingCategories = true;
    this.api
      .getCategories()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items) => {
          this.categories = items ?? [];
          this.loadingCategories = false;
          if (this.editingIdeaId) {
            this.loadIdeaForEdit(this.editingIdeaId);
          }

          if (this.categories.length === 0) {
            this.errorMessage = 'No categories available. Ask an admin to seed categories, then refresh.';
          }
        },
        error: () => {
          this.loadingCategories = false;
          this.errorMessage = 'Failed to load categories. Please ensure the API is running and try again.';
        }
      });

    this.form
      .get('categoryId')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const control = this.form.get('categoryId');
        if (!control) return;
        if (control.errors?.['invalidGuid']) {
          control.setErrors(null);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  saveDraft(): void {
    this.errorMessage = '';
    if (this.saving) {
      return;
    }

    this.saving = true;
    const title = String(this.form.value.title ?? '').trim();
    const categoryRaw = String(this.form.value.categoryId ?? '').trim();
    const categoryId = this.isGuid(categoryRaw) ? categoryRaw : null;
    const description = String(this.form.value.briefDescription ?? '').trim();
    const submitAnonymously = Boolean(this.form.value.submitAnonymously ?? false);
    const problemOpportunity = String(this.form.value.problemOpportunity ?? '').trim();
    const proposedSolution = String(this.form.value.proposedSolution ?? '').trim();
    const estimatedEffort = String(this.form.value.estimatedEffort ?? '').trim();
    const ownerDepartment = String(this.form.value.ownerDepartment ?? '').trim();
    const tagsText = String(this.form.value.tags ?? '');
    const impactScope = this.form.get('impactScope')?.value ?? null;
    const expectedImpactType = this.form.get('expectedImpactType')?.value ?? null;

    const tags = tagsText
      .split(',')
      .map((x: string) => x.trim())
      .filter((x: string) => x.length > 0);

    const challengeId = this.route.snapshot.queryParamMap.get('challengeId');
    const challengeIdValid = challengeId && this.isGuid(challengeId) ? challengeId : null;

    const payload = {
      title: title.length ? title : null,
      categoryId,
      challengeId: challengeIdValid,
      description: description.length ? description : null,
      problemOpportunity: problemOpportunity.length ? problemOpportunity : null,
      proposedSolution: proposedSolution.length ? proposedSolution : null,
      impactScope,
      expectedImpactType,
      estimatedEffort: estimatedEffort.length ? estimatedEffort : null,
      ownerDepartment: ownerDepartment.length ? ownerDepartment : null,
      tags: tags.length ? tags : null,
      submitAnonymously,
      isDraft: true
    };

    const request$ = this.editingIdeaId
      ? this.api.updateDraftIdea(this.editingIdeaId, payload)
      : this.api.createIdea(payload);

    request$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async () => {
          this.saving = false;
          await this.router.navigate(['/my-ideas']);
        },
        error: (err: unknown) => {
          this.saving = false;
          this.errorMessage = this.formatError(err) ?? 'Failed to save draft. Please try again.';
        }
      });
  }

  submitIdea(): void {
    this.errorMessage = '';

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.saving) {
      return;
    }

    this.saving = true;
    const title = String(this.form.value.title ?? '').trim();
    const categoryId = String(this.form.value.categoryId ?? '');
    const description = String(this.form.value.briefDescription ?? '').trim();
    const submitAnonymously = Boolean(this.form.value.submitAnonymously ?? false);
    const problemOpportunity = String(this.form.value.problemOpportunity ?? '').trim();
    const proposedSolution = String(this.form.value.proposedSolution ?? '').trim();
    const estimatedEffort = String(this.form.value.estimatedEffort ?? '').trim();
    const ownerDepartment = String(this.form.value.ownerDepartment ?? '').trim();
    const tagsText = String(this.form.value.tags ?? '');

    const impactScope = this.form.get('impactScope')?.value ?? null;
    const expectedImpactType = this.form.get('expectedImpactType')?.value ?? null;

    const tags = tagsText
      .split(',')
      .map((x: string) => x.trim())
      .filter((x: string) => x.length > 0);

    if (!this.isGuid(categoryId)) {
      this.saving = false;
      this.form.get('categoryId')?.setErrors({ invalidGuid: true });
      this.errorMessage = 'Please select a valid category.';
      return;
    }

    const challengeId = this.route.snapshot.queryParamMap.get('challengeId');
    const challengeIdValid = challengeId && this.isGuid(challengeId) ? challengeId : null;

    const payload = {
      title,
      categoryId,
      challengeId: challengeIdValid,
      description: description.length ? description : null,
      problemOpportunity: problemOpportunity.length ? problemOpportunity : null,
      proposedSolution: proposedSolution.length ? proposedSolution : null,
      impactScope,
      expectedImpactType,
      estimatedEffort: estimatedEffort.length ? estimatedEffort : null,
      ownerDepartment: ownerDepartment.length ? ownerDepartment : null,
      tags: tags.length ? tags : null,
      submitAnonymously,
      isDraft: false
    };

    const request$ = this.editingIdeaId
      ? this.api.updateDraftIdea(this.editingIdeaId, payload)
      : this.api.createIdea(payload);

    request$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (created) => {
          this.saving = false;
          await this.router.navigate(['/ideas', created.id]);
        },
        error: (err: unknown) => {
          this.saving = false;
          this.errorMessage = this.formatError(err) ?? 'Failed to submit idea. Please try again.';
        }
      });
  }

  private formatError(err: unknown): string | null {
    if (!(err instanceof HttpErrorResponse)) {
      return null;
    }

    // ASP.NET ValidationProblemDetails usually returns { errors: { field: [messages...] } }
    const body: any = err.error;
    if (body && typeof body === 'object') {
      const errors = body.errors;
      if (errors && typeof errors === 'object') {
        const firstKey = Object.keys(errors)[0];
        const firstVal = firstKey ? errors[firstKey] : null;
        if (Array.isArray(firstVal) && firstVal.length) {
          return String(firstVal[0]);
        }
      }

      if (typeof body.title === 'string' && body.title.length) {
        return body.title;
      }
    }

    if (typeof body === 'string' && body.length) {
      return body;
    }

    return err.message || null;
  }

  private isGuid(value: string): boolean {
    // Accepts standard GUID formats (case-insensitive) with hyphens.
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
  }

  private loadIdeaForEdit(ideaId: string): void {
    if (!this.isGuid(ideaId)) {
      this.errorMessage = 'Invalid idea id for editing.';
      return;
    }

    this.loadingIdea = true;
    this.errorMessage = '';
    this.api
      .getIdeaById(ideaId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (idea) => {
          const tags = Array.isArray(idea.tags) ? idea.tags.join(', ') : '';
          this.form.patchValue({
            title: idea.title ?? '',
            categoryId: idea.category?.id && this.isGuid(idea.category.id) ? idea.category.id : '',
            briefDescription: idea.description ?? '',
            problemOpportunity: idea.problemOpportunity ?? '',
            proposedSolution: idea.proposedSolution ?? '',
            impactScope: {
              myTeam: idea.impactScope?.myTeam ?? false,
              myDepartment: idea.impactScope?.myDepartment ?? false,
              companyWide: idea.impactScope?.companyWide ?? false,
              customers: idea.impactScope?.customers ?? false,
              product: idea.impactScope?.product ?? false
            },
            expectedImpactType: {
              timeSaved: idea.expectedImpactType?.timeSaved ?? false,
              costSaved: idea.expectedImpactType?.costSaved ?? false,
              revenue: idea.expectedImpactType?.revenue ?? false,
              quality: idea.expectedImpactType?.quality ?? false,
              riskReduction: idea.expectedImpactType?.riskReduction ?? false,
              employeeExperience: idea.expectedImpactType?.employeeExperience ?? false
            },
            estimatedEffort: idea.estimatedEffort ?? 'medium',
            ownerDepartment: idea.ownerDepartment ?? '',
            tags,
            submitAnonymously: idea.submitAnonymously ?? false
          });
          this.loadingIdea = false;
        },
        error: () => {
          this.errorMessage = 'Failed to load draft for editing.';
          this.loadingIdea = false;
        }
      });
  }
}
